/**
 * Effective rule-bundle resolution models (F20 org-chart-position-editor,
 * Story 3 / Phase 3b): "what applies to person P on day D?" — the winning
 * reglement rules ("strengste gewinnt") and the union of roles resolved along
 * position → org-unit path → root, with full provenance per entry.
 */

/**
 * Provenance of one effective rule/role entry: through WHICH of the person's
 * positions the assignment applies, and where it was attached.
 *
 * - `origin: 'direct'` — assignment sits on the position itself
 *   (`sourceOrgUnitId` is null).
 * - `origin: 'inherited'` — assignment sits on an org unit of the position's
 *   ancestor path (`sourceOrgUnitId` names that unit).
 * - `bundleId` — the bundle that carried the rule/role; null for a DIRECT
 *   role assignment (`core.org_role_assignments`).
 */
export interface EffectiveRuleSource {
  origin: 'direct' | 'inherited';
  /** The person's position the assignment reached (verdict provenance). */
  positionId: string;
  /** Org unit carrying an inherited assignment; null when `origin: 'direct'`. */
  sourceOrgUnitId: string | null;
  /** Bundle that carried the entry; null = direct role assignment. */
  bundleId: string | null;
  /** Effective-dated assignment row the entry came from. */
  assignmentId: string;
}

/**
 * One winning reglement rule of the effective set. Per kind exactly one
 * instance wins: along the kind's monotonicity axis the strictest value, for
 * kinds without an axis the most DIRECT source (position beats unit, nearer
 * unit beats farther). Ties are deterministic: smallest `positionId`, then
 * smallest `ruleInstanceId`.
 */
export interface EffectiveBundleRule {
  ruleInstanceId: string;
  kindKey: string;
  /** Provenance of the WINNING source (spec Story 3: verdict provenance). */
  source: EffectiveRuleSource;
}

/** One effective role — union semantics, ALL sources are listed (spec Story 3). */
export interface EffectiveBundleRole {
  roleId: string;
  sources: EffectiveRuleSource[];
}

/** Resolution result of `EffectiveRuleBundleService.resolve(personId, date)`. */
export interface EffectiveRuleBundleResolution {
  personId: string;
  /** Resolution key date (`YYYY-MM-DD`, as-of — NOT necessarily today). */
  date: string;
  /**
   * True when the person has NO position assignment on the date — only the
   * tenant-global statutory rules apply then (spec Story 3 AC).
   */
  unassigned: boolean;
  /** Winning reglement rules ("strengste gewinnt" per kind). Empty when unassigned. */
  rules: EffectiveBundleRule[];
  /** Union of roles from bundles and direct role assignments. Empty when unassigned. */
  roles: EffectiveBundleRole[];
}

// ---------------------------------------------------------------------------
// Endpoint response (GET time-tracking/persons/:personId/effective-rules)
// ---------------------------------------------------------------------------

/** Tenant-global statutory instance effective at the date — always applies. */
export interface EffectiveStatutoryRule {
  ruleInstanceId: string;
  kindKey: string;
}

/** Effective role enriched with its `role_permissions` resolution. */
export interface EffectiveRoleWithPermissions extends EffectiveBundleRole {
  /** Permission ids the role grants (rbac.role_permissions). */
  permissions: string[];
}

/** One unit of a provenance chain (own unit first, root last). */
export interface EffectiveRulesPathUnit {
  orgUnitId: string;
  name: string;
}

/** Provenance chain of one as-of position: position → unit path up to the root. */
export interface EffectiveRulesPath {
  positionId: string;
  positionName: string;
  /** Ancestor chain, own unit first, root last. */
  orgUnits: EffectiveRulesPathUnit[];
}

/**
 * Response of `GET time-tracking/persons/:personId/effective-rules?date=` —
 * the "Was gilt für diese Person?" panel data (spec Story 3): effective rule
 * set (statutory + winning bundle rules), roles incl. resolved permissions,
 * and the provenance chain per position.
 */
export interface PersonEffectiveRulesDto {
  personId: string;
  date: string;
  /** True = no position on the date; ONLY the statutory rules apply. */
  unassigned: boolean;
  statutoryRules: EffectiveStatutoryRule[];
  rules: EffectiveBundleRule[];
  roles: EffectiveRoleWithPermissions[];
  paths: EffectiveRulesPath[];
}
